import {
  afterNextRender,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  model,
  OnDestroy,
  OnInit,
  AfterViewInit,
  output,
  signal,
  type TemplateRef,
  untracked,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize, merge, Subject, switchMap, takeUntil } from 'rxjs';

import { ApiService, AppUserListRow } from '@core/services/api';
import { TabService } from '@core/services/tab.service';
import { extractApiErrorMessage } from '@core/utils/api-error-message';
import { exportFileBaseName } from '@core/utils/export-file-name';
import { exportGridRowsToXlsx, sumGridColumn } from '../../../utils/kl-grid-xlsx-export';
import { copyTextToClipboard, gridRowCopyText, isRowCopyShortcut } from '../../../utils/kl-grid-row-copy';
import { formatKlReportNumber } from '../../kl-report/kl-report-column-properties.util';
import { FilterPayload } from '../../kl-grid/grid.models';
import { KlConfirmDialogService } from '@core/services/kl-confirm-dialog.service';
import { ToastService } from '@core/services/toast.service';
import { UserActionAuthService } from '@core/services/user-action-auth.service';
import { UserEnvService } from '@core/services/user-env/user-env.service';
import { TranslateService } from '@ngx-translate/core';
import { MenuItem } from 'primeng/api';
import { ContextMenu } from 'primeng/contextmenu';
import { Menu } from 'primeng/menu';
import {
  Table,
  TableColResizeEvent,
  TableColumnReorderEvent,
  TablePageEvent,
  TableRowCollapseEvent,
  TableRowExpandEvent,
} from 'primeng/table';
import {
  ActionDto,
  cellRawValue,
  FastFilterDesignRecord,
  FilterableItem,
  GridActionContext,
  GridColumn,
  GridRowCellValue,
  KlStatusBadgeTone,
  isRowObject,
} from '../../kl-grid/grid.models';
import { ColumnFilterState } from '../../kl-grid/kl-column-filter/kl-column-filter';
import { KlGridColumnSettingsDrawer } from '../../kl-grid/kl-grid-column-settings-drawer/kl-grid-column-settings-drawer';
import { KlTransactionToolbar } from '../kl-transaction-toolbar/kl-transaction-toolbar';

@Component({
  selector: 'kl-transaction-grid',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './kl-transaction-grid.html',
  styleUrl: './kl-transaction-grid.scss',
})
export class KlTransactionGrid implements OnInit, AfterViewInit, OnDestroy {
  private readonly translate = inject(TranslateService);
  private readonly tabService = inject(TabService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly actionAuth = inject(UserActionAuthService);
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);
  private readonly api = inject(ApiService);
  private readonly userEnv = inject(UserEnvService);
  private readonly confirmDialog = inject(KlConfirmDialogService);
  private readonly toast = inject(ToastService);

  /** Avoid stacking multiple save prompts while one confirm dialog is still open. */
  private designSavePromptOpen = false;
  private destroyed = false;

  // ─── Signal inputs (readonly from parent) ───
  readonly rows = input.required<unknown[]>();
  readonly mapRows = input<((rows: unknown[]) => unknown[]) | null>(null);
  readonly columns = input.required<GridColumn[]>();
  readonly authNum = input.required<string>();
  readonly doExport = input(true);
  readonly doOther = input(true);
  readonly deferResizing = input(false);
  readonly sortable = input(true);
  readonly filterable = input(true);
  /** Per-column value checklist filter (independent of toolbar filter bar). */
  readonly columnFilters = input(true);
  /** PrimeNG header drag reorder (same pattern as `kl-report-datatable-results`). */
  readonly columnReorderEnabled = input(true);
  /** PrimeNG column resize handles on headers. */
  readonly columnResizeEnabled = input(true);
  readonly showGridSearch = input(false);
  readonly customFilter = input(false);
  readonly fullscreen = input(true);
  readonly actions = input<ActionDto[]>([]);
  readonly gridHeaderName = input('');
  readonly filterSelectItems = input<FilterableItem[]>([]);
  readonly endpointName = input('');
  readonly designParentModule = input('');
  readonly designModule = input('');
  readonly designKey = input('');
  readonly virtualScrollItemSize = input(31);
  readonly rowExpansionTemplate = input<TemplateRef<object> | null>(null);
  readonly gridDataKey = input<string>('id');
  readonly rowExpandMode = input<'single' | 'multiple'>('multiple');
  /** Columns whose footer shows the sum over the filtered rows (also exported); no footer row when empty. */
  readonly footerSumColumns = input<readonly string[]>([]);

  readonly clientPageSizes: number[] = [10, 20, 50, 100, 500, 1000];

  readonly filterApplied = output<FilterPayload>();
  readonly customFilterEvent = output<{ customFilter?: boolean; pageSize?: number }>();
  readonly refreshRequest = output<void>();
  /** Toolbar export — parent may fetch full row set before {@link exportToXlsx}. */
  readonly exportGrid = output<string>();

  readonly pageSize = model(10);
  /** Backend fetch threshold for “list all” (does not change {@link pageSize} input). */
  readonly allRowsPageSize = input(999);

  readonly tableData = computed(() => {
    const raw = this.rows();
    const mapFn = this.mapRows();
    if (!mapFn) {
      return raw as object[];
    }
    return mapFn([...raw]) as object[];
  });

  readonly disabledButton = computed(() => this.tableData().length === 0);
  readonly doDesign = computed(() => !!this.designKey().trim());

  readonly alertShow = signal(false);
  readonly alertType = signal('error');
  readonly alertMessage = signal('');
  readonly loading = signal(false);
  readonly loadingText = signal('loading');
  readonly recordFetchLoading = signal(false);

  readonly clientFirst = signal(0);
  readonly clientRowsPerPage = signal(10);

  readonly effectiveShowTablePaginator = computed(() => this.filteredData().length > 0);

  private static readonly TABLE_SCROLL_HEADER_PX = 42;
  private static readonly TABLE_SCROLL_FOOTER_PX = 32;
  private static readonly TABLE_SCROLL_MAX = '65vh';
  private static readonly TABLE_SCROLL_EMPTY = '120px';

  /** Shrink scroll body to current page rows; cap at {@link TABLE_SCROLL_MAX}. */
  readonly tableScrollHeight = computed(() => {
    const total = this.filteredData().length;
    if (total === 0) {
      return KlTransactionGrid.TABLE_SCROLL_EMPTY;
    }
    const offset = this.clientFirst();
    const pageSize = this.clientRowsPerPage();
    const pageRowCount = Math.max(1, Math.min(pageSize, total - offset));
    const footerPx = this.footerSumColumns().length > 0 ? KlTransactionGrid.TABLE_SCROLL_FOOTER_PX : 0;
    const px = KlTransactionGrid.TABLE_SCROLL_HEADER_PX + footerPx + pageRowCount * this.virtualScrollItemSize();
    return `min(${px}px, ${KlTransactionGrid.TABLE_SCROLL_MAX})`;
  });

  readonly gridPaginatorReportParams = computed(() => {
    const total = this.filteredData().length;
    const offset = this.clientFirst();
    const size = this.clientRowsPerPage();
    const first = total > 0 ? offset + 1 : 0;
    const last = total > 0 ? Math.min(offset + size, total) : 0;
    return { first, last, total };
  });

  // ─── Local mutable state ───
  /** PrimeNG controlled row expansion (`[expandedRowKeys]`); keys are `String(row[dataKey])`. */
  readonly expandedRowKeys = signal<Record<string, boolean>>({});

  readonly selectedRow = signal<any>(null);
  readonly currentItem = signal<any>({});
  readonly showFilterContainer = signal(false);
  readonly bulkDeleteEnabled = signal(false);
  readonly allChecked = signal(false);
  readonly selectedData = signal<any[]>([]);
  readonly sidebarVisible = signal(false);
  readonly drawerInitialTab = signal<'columns' | 'design'>('columns');
  readonly fullscreenActive = signal(false);

  readonly overlayAppendTo = computed(() => (this.fullscreenActive() ? this.host.nativeElement : 'body'));

  /** First load with no rows yet — full loader panel. */
  readonly showInitialLoader = computed(() => this.loading() && this.tableData().length === 0);

  /** Refresh / actions while rows are visible — overlay on top of the table. */
  readonly showLoadingOverlay = computed(() => this.loading() && this.tableData().length > 0);

  readonly mergedCustomToolbar = computed((): ActionDto[] => {
    const items: ActionDto[] = [];
    if (this.doDesign()) {
      items.push({
        slug: 'save_design_or_load',
        icon: 'ph ph-sliders',
        gridLevel: true,
        method: () => this.openDesign(),
      });
    }
    const viewSource = this.actions().find((a) => a.slug === 'view_source');
    if (viewSource && this.isActionVisible(viewSource)) {
      items.push({
        ...viewSource,
        icon: viewSource.icon ?? 'ph ph-arrow-square-out',
      });
    }
    return items;
  });

  readonly textToFilter = signal('');
  readonly filterType = signal<FilterableItem | null>(null);
  readonly filterExpression = signal('LIKE');

  readonly filterExpressionOptions: { value: string; label: string }[] = [
    { value: 'LIKE', label: 'filter_LIKE' },
    { value: 'NOT_LIKE', label: 'filter_NOT_LIKE' },
    { value: 'EQ', label: 'filter_EQ' },
    { value: 'NOT_EQ', label: 'filter_NOT_EQ' },
    { value: 'START_WITH', label: 'filter_START_WITH' },
    { value: 'END_WITH', label: 'filter_END_WITH' },
    { value: 'GT', label: 'filter_GT' },
    { value: 'GE', label: 'filter_GE' },
    { value: 'LT', label: 'filter_LT' },
    { value: 'LE', label: 'filter_LE' },
  ];

  /** Id of the current user's saved fast-filter `design` record, once loaded/saved. */
  private fastFilterRecordId: number | string | null = null;
  readonly fastFilterSaving = signal(false);
  readonly fastFilterMenuModel = computed<MenuItem[]>(() => [
    {
      label: this.translate.instant('save'),
      icon: 'ph ph-floppy-disk',
      command: () => this.saveFastFilter(),
    },
  ]);

  readonly filterMenuModel = signal<MenuItem[]>([]);
  readonly otherMenuModel = signal<MenuItem[]>([]);
  readonly contextMenuModel = signal<MenuItem[]>([]);
  readonly hasOtherMenu = signal(false);

  /** Column filter states keyed by binding (signal for reactivity in filteredData). */
  readonly columnFilterStates = signal(new Map<string, ColumnFilterState>());

  /** Client-side sort from column header menu (current page / loaded rows). */
  readonly clientSortBinding = signal<string | null>(null);
  readonly clientSortDesc = signal(false);

  /** Local column definitions (may differ from input when drawer toggles visibility). */
  readonly effectiveColumns = signal<GridColumn[]>([]);

  /**
   * PrimeNG `p-table` [columns] for reorder (mutated in place). Only columns with `binding`
   * participate; order tracks {@link visibleColumns}.
   */
  readonly tableColumnMeta = signal<{ field: string }[]>([]);

  /** Visible columns (after applying column visibility). */
  readonly visibleColumns = computed(() => this.effectiveColumns().filter((c) => c.visible !== false));

  /** Settings column (gear / row expand) + data columns (empty / expansion `colSpan`). */
  readonly tableLeadColSpan = computed(() => this.visibleColumns().length + 1);

  /** Data filtered by column value-list filters, then client-sorted when requested. */
  readonly filteredData = computed(() => this.applyColumnFiltersAndClientSort(this.tableData()));

  /** Footer text per summed binding, formatted like the column's cells. */
  readonly footerSums = computed(() => {
    const sumColumns = this.footerSumColumns();
    const rows = this.filteredData();
    const sums = new Map<string, string>();
    for (const col of this.visibleColumns()) {
      if (col.binding && sumColumns.includes(col.binding)) {
        const sum = sumGridColumn(rows, col.binding);
        const digits = col.displayFractionDigits;
        sums.set(col.binding, digits == null ? String(sum) : formatKlReportNumber(sum, digits, !!col.numberGrouping));
      }
    }
    return sums;
  });

  /** Column filters + client sort on the full row set (not limited by table paginator page size). */
  private applyColumnFiltersAndClientSort(rows: object[]): object[] {
    let next = rows;
    const states = this.columnFilterStates();
    const activeFilters = Array.from(states.values()).filter((f) => f.active);
    if (activeFilters.length) {
      next = next.filter((row) => activeFilters.every((f) => f.checkedValues.has(cellRawValue(row, f.binding))));
    }
    const sortBinding = this.clientSortBinding();
    if (!sortBinding) {
      return next;
    }
    const colMap = new Map(this.effectiveColumns().map((c) => [c.binding ?? '', c]));
    const col = colMap.get(sortBinding);
    const desc = this.clientSortDesc();
    return [...next].sort((a, b) => {
      const cmp = this.compareRowsForSort(a, b, sortBinding, col);
      return desc ? -cmp : cmp;
    });
  }

  private static isXlsxExportType(type: string): boolean {
    const t = type.trim().toLowerCase();
    return t === 'xlsx' || t === 'excel';
  }

  readonly contextMenuRef = viewChild.required<ContextMenu>('contextMenu');
  readonly columnSettingsDrawer = viewChild<KlGridColumnSettingsDrawer>('columnSettingsDrawer');
  readonly gridToolbar = viewChild<KlTransactionToolbar>('gridToolbar');
  readonly tableRef = viewChild<Table>('dt');

  /** Passed to the column settings drawer so save snapshots live header widths. */
  readonly captureColumnWidths = (): void => {
    this.syncColumnWidthsFromHeader();
  };

  private readonly destroy$ = new Subject<void>();

  /** PrimeNG `{currentPage} / {totalPages}` template with translated "page" label. */
  readonly pageReportTemplate = computed(() => {
    const label = this.translate.instant('page');
    return `${label} {currentPage} / {totalPages}`;
  });

  constructor() {
    const syncFullscreen = (): void => {
      const active =
        document.fullscreenElement ??
        (document as Document & { webkitFullscreenElement?: Element | null }).webkitFullscreenElement;
      this.fullscreenActive.set(active === this.host.nativeElement);
    };

    document.addEventListener('fullscreenchange', syncFullscreen);
    document.addEventListener('webkitfullscreenchange', syncFullscreen);

    this.destroyRef.onDestroy(() => {
      document.removeEventListener('fullscreenchange', syncFullscreen);
      document.removeEventListener('webkitfullscreenchange', syncFullscreen);
      if (
        document.fullscreenElement === this.host.nativeElement ||
        (document as Document & { webkitFullscreenElement?: Element | null }).webkitFullscreenElement ===
          this.host.nativeElement
      ) {
        void this.exitNativeFullscreen();
      }
    });

    effect(() => {
      this.effectiveColumns.set([...this.columns()]);
    });

    effect(() => {
      const vis = this.visibleColumns();
      untracked(() => {
        const keys = vis.map((c) => c.binding).filter((b): b is string => !!b);
        const cur = this.tableColumnMeta().map((m) => m.field);
        if (cur.length === keys.length && cur.every((k, i) => k === keys[i])) {
          return;
        }
        this.tableColumnMeta.set(keys.map((field) => ({ field })));
      });
    });

    effect(() => {
      const items = this.filterSelectItems();
      if (items?.length && !this.filterType()) {
        this.filterType.set(items[0]);
      }
    });

    effect(() => {
      const rows = this.tableData();
      const hasExpansion = this.rowExpansionTemplate();
      if (!hasExpansion) {
        return;
      }
      untracked(() => {
        if (!rows?.length && Object.keys(this.expandedRowKeys()).length > 0) {
          this.expandedRowKeys.set({});
        }
      });
    });

    effect(() => {
      const rows = this.tableData();
      untracked(() => {
        this.clientFirst.set(0);
        if (rows?.length) {
          this.currentItem.set(rows[0]);
          this.selectedRow.set(rows[0]);
        }
      });
    });

    effect(() => {
      void this.actions();
      void this.doDesign();
      this.buildMenuItems();
      this.cdr.markForCheck();
    });
  }

  /** Toolbar / context menu entry for a row action (flat or with nested `children`). */
  private isActionVisible(action: ActionDto): boolean {
    if (action.isRowVisible && !action.isRowVisible(this.currentItem())) {
      return false;
    }
    if (!action.authNum) {
      return true;
    }
    return this.actionAuth.canShowAction(action.authNum);
  }

  private actionToMenuItem(a: ActionDto, defaultIcon?: string): MenuItem {
    if (a.children?.length) {
      return {
        label: this.translate.instant(a.slug_2 ?? a.slug ?? ''),
        icon: a.icon ?? defaultIcon,
        items: a.children.map((c) => ({
          label: this.translate.instant(c.slug_2 ?? c.slug ?? ''),
          icon: c.icon ?? defaultIcon ?? 'ph ph-list',
          command: () => this.doOtherAction(a, c),
        })),
      };
    }
    return {
      label: this.translate.instant(a.slug_2 ?? a.slug ?? ''),
      icon: a.icon ?? defaultIcon,
      command: () => this.doOtherAction(a),
    };
  }

  ngOnInit(): void {
    const items = this.filterSelectItems();
    if (items?.length && !this.filterType()) {
      this.filterType.set(items[0]);
    }
    this.buildMenuItems();
    this.loadFastFilter();

    merge(this.translate.onLangChange, this.translate.onTranslationChange)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.buildMenuItems();
        this.cdr.markForCheck();
      });
  }

  ngAfterViewInit(): void {
    if (this.hasDesignSaveTarget()) {
      this.columnSettingsDrawer()?.loadInitialGridDesign();
    }
  }

  /** Primary key for toolbar CRUD: `id` if set, else value at {@link gridDataKey} (supports dotted paths). */
  private currentItemEntityId(): GridRowCellValue {
    const row = this.currentItem();
    if (!isRowObject(row)) {
      return undefined;
    }
    const direct = row['id'];
    if (direct != null && direct !== '') {
      return direct;
    }
    const key = this.gridDataKey();
    if (key && key !== 'id') {
      return this.resolveDataKeyValue(row, key);
    }
    return undefined;
  }

  /** Nested property read for {@link gridDataKey} (e.g. `id` or `entity.id`). */
  private resolveDataKeyValue(
    row: Record<string, GridRowCellValue> | null | undefined,
    keyPath: string
  ): GridRowCellValue {
    if (!row || !keyPath) {
      return undefined;
    }
    const parts = keyPath.split('.');
    let cur: GridRowCellValue = row;
    for (const p of parts) {
      if (!isRowObject(cur)) {
        return undefined;
      }
      cur = cur[p];
    }
    return cur;
  }

  /**
   * Unique id for a grid row, used to enable checkboxes and match selections.
   * Uses `row.id` when present; otherwise reads the field named by {@link gridDataKey}
   * (e.g. `transaction_message_query_id` on restaurant end-of-day).
   */
  rowEntityId(row: Record<string, GridRowCellValue> | null | undefined): GridRowCellValue {
    if (!isRowObject(row)) {
      return undefined;
    }
    const direct = row['id'];
    if (direct != null && direct !== '') {
      return direct;
    }
    const key = this.gridDataKey();
    if (key && key !== 'id') {
      return this.resolveDataKeyValue(row, key);
    }
    return undefined;
  }

  /**
   * Form field name for the row checkbox column (header select-all uses the same field).
   * Returns the first visible column with `multi_select: true`, e.g. `selected` or `bulkDeleteSelection`.
   */
  private multiSelectColumnBinding(): string | null {
    const col = this.visibleColumns().find((c) => c.multi_select && c.binding);
    return col?.binding ?? null;
  }

  private rowExpansionKey(row: object): string | null {
    if (!isRowObject(row)) {
      return null;
    }
    const path = this.gridDataKey();
    const v = this.resolveDataKeyValue(row, path);
    if (v === undefined || v === null) {
      return null;
    }
    return String(v);
  }

  onRowExpand(event: TableRowExpandEvent): void {
    if (!this.rowExpansionTemplate()) {
      return;
    }
    const k = this.rowExpansionKey(event.data);
    if (!k) {
      return;
    }
    const next = this.rowExpandMode() === 'single' ? { [k]: true } : { ...this.expandedRowKeys(), [k]: true };
    this.expandedRowKeys.set(next);
  }

  onRowCollapse(event: TableRowCollapseEvent): void {
    if (!this.rowExpansionTemplate()) {
      return;
    }
    const k = this.rowExpansionKey(event.data);
    if (!k) {
      return;
    }
    const next = { ...this.expandedRowKeys() };
    delete next[k];
    this.expandedRowKeys.set(next);
  }

  onTablePage(event: TablePageEvent): void {
    if (this.rowExpansionTemplate() && Object.keys(this.expandedRowKeys()).length > 0) {
      this.expandedRowKeys.set({});
    }
    this.clientFirst.set(event.first ?? 0);
    const rows = event.rows ?? this.clientRowsPerPage();
    this.clientRowsPerPage.set(rows);
  }

  onColReorder(_event: TableColumnReorderEvent): void {
    if (!this.columnReorderEnabled()) {
      return;
    }
    const vis = this.visibleColumns();
    const newBindings = this.tableColumnMeta().map((m) => m.field);
    const full = [...this.effectiveColumns()];
    const map = new Map(full.filter((c) => c.binding).map((c) => [c.binding!, c] as const));

    let bi = 0;
    const rebuiltVis = vis.map((c) => {
      if (!c.binding) {
        return c;
      }
      const next = map.get(newBindings[bi++]);
      return next ?? c;
    });

    let vi = 0;
    const merged = full.map((c) => {
      if (c.visible === false) {
        return c;
      }
      return rebuiltVis[vi++]!;
    });

    this.effectiveColumns.set(merged);
    this.columnSettingsDrawer()?.markLiveLayoutTouched();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ─── Row selection ───

  onRowSelect(event: any): void {
    this.currentItem.set(event.data ?? event);
    this.selectedRow.set(this.currentItem());
  }

  onRowDblClick(row: any): void {
    this.currentItem.set(row);
    this.selectedRow.set(row);
  }

  onRowKeydown(event: KeyboardEvent, row: object): void {
    if (!isRowCopyShortcut(event)) return;
    void copyTextToClipboard(gridRowCopyText(row, this.visibleColumns(), this.translate));
  }

  onRowContextMenu(event: MouseEvent, row: any): void {
    this.currentItem.set(row);
    this.selectedRow.set(row);
    this.buildMenuItems();
    this.cdr.markForCheck();
    if (this.contextMenuModel().length > 0) {
      event.preventDefault();
      setTimeout(() => this.contextMenuRef()?.show(event), 0);
    }
  }

  // ─── Column filter handling ───

  getColumnFilterState(col: GridColumn): ColumnFilterState | null {
    return this.columnFilterStates().get(col.binding!) ?? null;
  }

  onColumnFilterApply(state: ColumnFilterState): void {
    const next = new Map(this.columnFilterStates());
    next.set(state.binding, state);
    this.columnFilterStates.set(next);
  }

  onColumnFilterClear(binding: string): void {
    const next = new Map(this.columnFilterStates());
    next.delete(binding);
    this.columnFilterStates.set(next);
  }

  onColumnSortAsc(binding: string): void {
    this.clientSortBinding.set(binding);
    this.clientSortDesc.set(false);
  }

  onColumnSortDesc(binding: string): void {
    this.clientSortBinding.set(binding);
    this.clientSortDesc.set(true);
  }

  private compareRowsForSort(a: object, b: object, binding: string, col: GridColumn | undefined): number {
    if (!isRowObject(a) || !isRowObject(b)) {
      return 0;
    }
    const va = cellRawValue(a, binding);
    const vb = cellRawValue(b, binding);
    if (va == null && vb == null) return 0;
    if (va == null) return -1;
    if (vb == null) return 1;
    if (col?.format === 'date' || col?.format === 'date-time') {
      const da = new Date(va as string | number | Date).getTime();
      const db = new Date(vb as string | number | Date).getTime();
      if (Number.isNaN(da) || Number.isNaN(db)) {
        return String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
      }
      return da === db ? 0 : da < db ? -1 : 1;
    }
    if (typeof va === 'number' && typeof vb === 'number') {
      return va === vb ? 0 : va < vb ? -1 : 1;
    }
    return String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
  }

  // ─── Toolbar actions ───

  private forceCloseMenuOverlay(overlay: Menu | ContextMenu | undefined): void {
    if (!overlay) return;
    overlay.hide();
    // PrimeNG popup overlays only clean up when the leave animation completes.
    // When navigation detaches this view mid-animation (LayoutRouteReuseStrategy),
    // the overlay is stranded in <body>. Force-remove it now.
    const container = overlay.container;
    container?.parentNode?.removeChild(container);
    overlay.container = undefined;
  }

  private closeActionMenus(): void {
    const toolbar = this.gridToolbar();
    this.forceCloseMenuOverlay(toolbar?.otherMenu);
    this.forceCloseMenuOverlay(toolbar?.filterMenu);
    this.forceCloseMenuOverlay(this.contextMenuRef());
  }

  refresh(): void {
    this.refreshRequest.emit();
  }

  submit(): void {
    this.filterApplied.emit(this.prepareFilterObject());
  }

  /** Fetch full row set for current filter; {@link pageSize} input stays unchanged. */
  submitListAll(): void {
    const payload = this.prepareFilterObject();
    payload.filterInstance.query.paging.limit = this.allRowsPageSize();
    this.filterApplied.emit(payload);
  }

  // ─── Fast filter (search field / operator / page size) save & restore ──────

  private fastFilterCompanyId(): number | null {
    const id = this.userEnv.env()?.company?.id;
    if (id == null) return null;
    const n = Number(id);
    return Number.isFinite(n) ? n : null;
  }

  fastFilterQueryActive(): boolean {
    return !!this.designModule().trim();
  }

  /** Design key of this grid's fast filter (`<module>_FAST_FILTER`). */
  private fastFilterKey(): string {
    return `${this.designModule().trim()}_FAST_FILTER`;
  }

  /** Restores the user's saved search field / operator / page size for this grid, then re-fetches. */
  private loadFastFilter(): void {
    if (!this.fastFilterQueryActive()) return;
    // kl-grid skips its own initial fetch here, so submit() must fire on every path (not just found).
    const companyId = this.fastFilterCompanyId();
    if (!companyId) {
      this.submit();
      return;
    }

    const module = this.designModule().trim();
    const key = this.fastFilterKey();
    this.api
      .getFilteredData<FastFilterDesignRecord[]>('design', {
        parent: { module: this.designParentModule().trim() },
        module,
        key,
        type: 'FILTER',
        company_id: companyId,
      })
      .subscribe({
        next: (res) => {
          // The response can also hold the module's grid designs; saving must never overwrite one of them.
          const record = res?.data?.find(
            (row) => row.module === module && row.key === key && (!row.type || row.type === 'FILTER')
          );
          if (record) {
            this.fastFilterRecordId = record.id ?? null;
            const content = this.parseFastFilterContent(record.content ?? '');
            if (content) {
              const match = this.filterSelectItems().find((f) => f.name === content.filter_property);
              if (match) this.filterType.set(match);
              if (content.filter_expression_operator) this.filterExpression.set(content.filter_expression_operator);
              // filter_limit can come back as a string after JSON round-trip.
              const limit = Number(content.filter_limit);
              if (Number.isFinite(limit) && limit > 0) this.pageSize.set(limit);
            }
          }
          this.submit();
        },
        error: () => this.submit(),
      });
  }

  private parseFastFilterContent(
    content: string
  ): { filter_property?: string; filter_expression_operator?: string; filter_limit?: number } | null {
    try {
      return JSON.parse(content);
    } catch {
      return null;
    }
  }

  /** Persists the current search field / operator / page size choice for the current user. */
  saveFastFilter(): void {
    if (this.fastFilterSaving() || !this.fastFilterQueryActive()) return;
    const companyId = this.fastFilterCompanyId();
    if (!companyId) return;

    const ft = this.filterType();
    const content = JSON.stringify({
      filter_property: ft?.name,
      filter_expression_operator: ft && !ft.disabledExpression ? this.filterExpression() : undefined,
      filter_limit: this.pageSize(),
    });
    const key = this.fastFilterKey();

    this.fastFilterSaving.set(true);
    // Fast filters are saved per user: the backend only returns `USER` records listing the requester's id.
    this.api
      .getAllData<AppUserListRow>('user/my')
      .pipe(
        switchMap((me) => {
          const payload = {
            parent: { module: this.designParentModule().trim() },
            module: this.designModule().trim(),
            key,
            level: 'USER',
            level_values: [String(me.id)],
            level_default: 'DEFAULT',
            description: key,
            type: 'FILTER',
            company_id: companyId,
            content,
          };
          return this.fastFilterRecordId
            ? this.api.updateData<FastFilterDesignRecord>('design', { ...payload, id: this.fastFilterRecordId })
            : this.api.addData<FastFilterDesignRecord>('design', payload);
        }),
        finalize(() => this.fastFilterSaving.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (saved) => {
          this.fastFilterRecordId = saved?.id ?? this.fastFilterRecordId;
          this.toast.success({ detail: 'success_message' });
        },
        error: (err) => this.handleError(err),
      });
  }

  prepareFilterObject(): any {
    const filterInstance: any = {
      filter: {},
      parameters: [],
      query: { paging: { limit: this.pageSize() || 10, fetch: 'EAGER' } },
    };

    const ft = this.filterType();
    const txt = this.textToFilter();
    if (ft && txt) {
      const name = ft.name;
      const key = ft.objectKey;
      const type = ft.type;

      if (key) {
        if (type === 'array') {
          filterInstance.filter[name] = [{ [key]: txt }];
        } else if (type === 'object') {
          filterInstance.filter[name] = { [key]: txt };
        } else {
          filterInstance.filter[name] = filterInstance.filter[name] || {};
          filterInstance.filter[name][key] = txt;
        }
      } else {
        filterInstance.filter[name] = type === 'number' ? parseInt(txt, 10) : txt;
      }
    }

    const expr = this.filterExpression();
    if (expr && ft && !ft.disabledExpression) {
      filterInstance.parameters.push({
        name: ft.parameterName || ft.name,
        expression: { operator: expr },
      });
    }

    return {
      filterInstance,
      filterArgs: {
        type: ft,
        expression: expr,
        size: this.pageSize(),
      },
    };
  }

  exportAs(type: string): void {
    if (!KlTransactionGrid.isXlsxExportType(type)) {
      return;
    }
    this.exportGrid.emit(type);
  }

  /** XLSX export using all given rows after column filters / sort (not the paginator page slice). */
  exportToXlsx(sourceRows?: unknown[]): void {
    let rows: object[];
    if (sourceRows != null) {
      const mapFn = this.mapRows();
      rows = (mapFn ? mapFn([...sourceRows]) : sourceRows) as object[];
    } else {
      rows = this.tableData();
    }
    const exportRows = this.applyColumnFiltersAndClientSort(rows);
    const name = exportFileBaseName(this.translate, this.tabService, this.gridHeaderName() || 'export');
    exportGridRowsToXlsx({
      columns: this.visibleColumns(),
      rows: exportRows,
      name,
      translate: this.translate,
      onError: (message) => this.showAlert(message, 'error'),
      footerSumColumns: this.footerSumColumns(),
    });
  }

  toggleFilterContainer(): void {
    this.showFilterContainer.set(!this.showFilterContainer());
  }

  customFilterOpen(): void {
    this.closeActionMenus();
    this.customFilterEvent.emit({ customFilter: true, pageSize: this.pageSize() });
  }

  buildMenuItems(): void {
    this.filterMenuModel.set([
      {
        label: this.translate.instant('detail_filter'),
        icon: 'ph ph-funnel',
        command: () => this.customFilterOpen(),
      },
    ]);

    const otherItems: MenuItem[] = [];
    const toolbarActions = (this.actions() ?? []).filter((a) => !a.notShowInToolbar && this.isActionVisible(a));
    for (const a of toolbarActions) {
      if (a.separator) {
        otherItems.push({ separator: true });
      } else {
        otherItems.push(this.actionToMenuItem(a));
      }
    }
    this.otherMenuModel.set(otherItems);
    this.hasOtherMenu.set(this.doOther() && otherItems.length > 0);

    const ctxItems: MenuItem[] = [];
    const contextActions = (this.actions() ?? []).filter((a) => !a.notShowInContextMenu && this.isActionVisible(a));
    for (const a of contextActions) {
      if (a.separator) {
        ctxItems.push({ separator: true });
      } else {
        ctxItems.push(this.actionToMenuItem(a, 'ph ph-list'));
      }
    }
    this.contextMenuModel.set(ctxItems);
  }

  onAlertClose(): void {
    this.alertShow.set(false);
    this.alertMessage.set('');
  }

  onRowButtonClick(column: any, row: any, event: MouseEvent): void {
    event.stopPropagation();
    if (!row) return;
    this.currentItem.set(row);
    this.selectedRow.set(row);
    const name = column.rowActionName as string | undefined;
    if (!name) return;
    const action = this.actions().find((a) => a.slug === name);
    if (action) {
      this.doOtherAction(action);
    }
  }

  startBulkDelete(): void {
    const hasCheckbox = this.effectiveColumns().some(
      (c) => c.binding === 'bulkDeleteSelection' || c.binding === 'selected'
    );

    if (!hasCheckbox) {
      const checkboxColumn: GridColumn = {
        header: 'select',
        binding: 'bulkDeleteSelection',
        width: '60px',
        visible: true,
        multi_select: true,
      };
      this.effectiveColumns.set([checkboxColumn, ...this.effectiveColumns()]);
    }

    this.bulkDeleteEnabled.set(true);
    this.selectedData.set([]);
    this.allChecked.set(false);
  }

  confirmBulkDelete(): void {
    this.cancelBulkDelete();
  }

  cancelBulkDelete(): void {
    this.bulkDeleteEnabled.set(false);
    this.selectedData.set([]);
    this.allChecked.set(false);

    const filtered = this.effectiveColumns().filter((c) => c.binding !== 'bulkDeleteSelection');
    this.effectiveColumns.set(filtered);

    this.cdr.markForCheck();
  }

  toggleAllCheckboxes(): void {
    const binding = this.multiSelectColumnBinding();
    if (!binding) {
      return;
    }

    const rows = this.tableData();
    const checked = this.allChecked();
    const selected: any[] = [];

    rows.forEach((rowObj) => {
      const row = rowObj as Record<string, GridRowCellValue>;
      if (this.rowEntityId(row) == null) {
        return;
      }
      row[binding] = checked;
      if (checked) {
        selected.push(row);
      }
    });

    this.selectedData.set(selected);
    this.cdr.markForCheck();
  }

  onCheckboxChange(event: { checked?: boolean }, row: Record<string, any>): void {
    const binding = this.multiSelectColumnBinding();
    if (!binding) {
      return;
    }

    const entityId = this.rowEntityId(row);
    if (entityId == null) {
      return;
    }

    const current = [...this.selectedData()];
    if (event.checked) {
      const exists = current.some((r) => this.rowEntityId(r) === entityId);
      if (!exists) {
        current.push(row);
      }
    } else {
      const index = current.findIndex((r) => this.rowEntityId(r) === entityId);
      if (index !== -1) {
        current.splice(index, 1);
      }
      this.allChecked.set(false);
    }

    this.selectedData.set(current);

    const selectable = this.tableData().filter((r) => this.rowEntityId(r as Record<string, GridRowCellValue>) != null);
    const allSelected =
      selectable.length > 0 &&
      selectable.every((r) => !!(r as Record<string, GridRowCellValue>)[binding]);
    if (allSelected) {
      this.allChecked.set(true);
    }

    this.cdr.markForCheck();
  }

  onCustomToolbarAction(action: ActionDto): void {
    if (action.gridLevel) {
      this.closeActionMenus();
      action.method?.({ item: this.currentItem() ?? {} });
      return;
    }
    this.doOtherAction(action);
  }

  doOtherAction(action: ActionDto, childAction?: ActionDto): void {
    this.closeActionMenus();
    const item = this.currentItem();
    if (!item || Object.keys(item).length === 0) return;
    const ctxBase: GridActionContext = {
      item,
      refresh: () => this.refresh(),
      gridLoading: {
        show: (text: string) => this.showLoading(text),
        hide: () => this.hideLoading(),
      },
      showAlert: (message, type) => this.showAlert(message, type),
      handleError: (error) => this.handleError(error),
    };
    if (!action.children) {
      if (!action.separator) action.method?.(ctxBase);
    } else if (childAction) {
      childAction.method?.(ctxBase);
    }
  }

  preventNonNumeric(e: KeyboardEvent): void {
    const allowed = ['Backspace', 'ArrowLeft', 'ArrowRight', 'Delete', 'Tab'];
    if (!/^\d$/.test(e.key) && !allowed.includes(e.key)) e.preventDefault();
  }

  commitPageSize(e: Event): void {
    const raw = Number((e.target as HTMLInputElement).value);
    const value = Math.max(1, Math.min(999, raw || this.pageSize()));
    this.pageSize.set(value);
  }

  refreshFlexGrid(): void {
    this.cdr.markForCheck();
  }

  openSidebar(tab: 'columns' | 'design' = 'columns'): void {
    this.drawerInitialTab.set(tab);
    this.sidebarVisible.set(true);
  }

  openDesign(): void {
    this.openSidebar('design');
  }

  toggleFullscreen(): void {
    if (this.fullscreenActive()) {
      void this.exitNativeFullscreen();
      return;
    }

    const el = this.host.nativeElement as HTMLElement & {
      webkitRequestFullscreen?: () => Promise<void> | void;
    };
    if (el.requestFullscreen) {
      void el.requestFullscreen();
    } else if (el.webkitRequestFullscreen) {
      void el.webkitRequestFullscreen();
    }
  }

  private exitNativeFullscreen(): Promise<void> | void {
    const doc = document as Document & {
      webkitExitFullscreen?: () => Promise<void> | void;
    };
    if (document.exitFullscreen) {
      return document.exitFullscreen();
    }
    if (doc.webkitExitFullscreen) {
      return doc.webkitExitFullscreen();
    }
  }

  onColumnDrawerResetToDefault(): void {
    this.effectiveColumns.set([...this.columns()]);
    this.columnFilterStates.set(new Map());
    this.clientSortBinding.set(null);
    this.clientSortDesc.set(false);
    this.clearColumnWidthsFromTable();
    queueMicrotask(() => this.columnSettingsDrawer()?.syncAfterParentReset());
  }

  onColumnsChanged(updatedColumns: GridColumn[]): void {
    this.effectiveColumns.set(updatedColumns);
    // The initial design fetch is not cancelled on destroy, so it can answer after the tab
    // closed — registering a render hook on the destroyed injector would throw.
    if (this.destroyed) {
      return;
    }
    // Widths are enforced from the rendered header, so wait for the new cells: visibility
    // and order changes shift the `th:nth-child(n)` indices PrimeNG writes rules against.
    afterNextRender(() => this.applyColumnWidthsToTable(), { injector: this.injector });
  }

  onColResize(_event: TableColResizeEvent): void {
    this.syncColumnWidthsFromHeader();
    this.promptSaveDesignAfterResize();
  }

  /**
   * Copy live header pixel widths from the DOM into {@link effectiveColumns}.
   *
   * Legacy `form-entry-grid` persisted every column width at save time; PrimeNG
   * `p-table` column resize only updates rendered `<th>` sizes, so the in-memory
   * column model can lag behind what the user sees. This method closes that gap by
   * reading `offsetWidth` from each `th.kl-grid-header-cell` in header order and
   * writing the values back onto the matching bindings in `effectiveColumns`.
   *
   * Used from:
   * - {@link onColResize} immediately after the user drags a resize handle
   * - {@link captureColumnWidths} (passed to the column settings drawer) right before
   *   a grid design is saved, so the payload includes all current widths—not only
   *   the column that was last resized
   *
   * Header cells are matched to {@link visibleColumns} by index (lead checkbox /
   * gear column is excluded from `visibleColumns`, so indices stay aligned with
   * data headers). Widths of `*` / flex columns are stored as numeric pixels once
   * measured. No-op when the table is not rendered yet or nothing changed.
   */
  syncColumnWidthsFromHeader(): void {
    const headerCells = Array.from(
      this.host.nativeElement.querySelectorAll('thead tr th.kl-grid-header-cell')
    ) as HTMLElement[];
    const vis = this.visibleColumns();
    if (headerCells.length === 0 || vis.length === 0) {
      return;
    }

    const cols = [...this.effectiveColumns()];
    let changed = false;

    for (const [index, headerCell] of headerCells.entries()) {
      const col = vis[index];
      if (!col?.binding) {
        continue;
      }

      const width = headerCell.offsetWidth;
      if (!Number.isFinite(width) || width <= 0) {
        continue;
      }

      const idx = cols.findIndex((c) => c.binding === col.binding);
      if (idx < 0) {
        continue;
      }

      const current = this.resolveColumnWidth(cols[idx].width);
      if (current !== width) {
        cols[idx] = { ...cols[idx], width };
        changed = true;
      }
    }

    if (changed) {
      this.effectiveColumns.set(cols);
      this.columnSettingsDrawer()?.markLiveLayoutTouched();
    }
  }

  /**
   * Enforce {@link effectiveColumns} widths the way PrimeNG does after a manual resize.
   *
   * `p-table` does not size columns from the `th` `[style.width]` binding: in
   * `columnResizeMode="expand"` it injects a stylesheet of
   * `th:nth-child(n) { width: Xpx !important }` rules and sets an explicit width on the
   * `<table>` element (`Table.onColumnResizeEnd`, replayed by `Table.restoreColumnWidths`
   * for stateful tables). Without that, `table-layout: auto` redistributes the available
   * width and a restored grid design renders as if it had been ignored. Driving PrimeNG's
   * own restore path keeps a design applied from the drawer identical to a drag-resize.
   */
  private applyColumnWidthsToTable(): void {
    const table = this.tableRef();
    if (!table?.tableViewChild?.nativeElement) {
      return;
    }

    // Positional over the header row to match PrimeNG's `th:nth-child(n)` rules: index 0 is
    // the leading gear / row-expand column, which `visibleColumns` does not include.
    const headerRow: HTMLTableRowElement | null = (this.host.nativeElement as HTMLElement).querySelector('thead tr');
    const headerCells = Array.from(headerRow?.cells ?? []) as HTMLElement[];
    if (headerCells.length === 0) {
      return;
    }

    const vis = this.visibleColumns();
    const widths = headerCells.map((cell, index) => {
      const col = index === 0 ? undefined : vis[index - 1];
      return this.resolveColumnWidth(col?.width) ?? cell.offsetWidth;
    });
    // A zero width would be enforced with `!important` and collapse the column for good.
    if (widths.some((width) => !Number.isFinite(width) || width <= 0)) {
      return;
    }

    // `restoreColumnWidths()` only creates a style element, so drop the previous one first.
    table.destroyStyleElement();
    table.columnWidthsState = widths.join(',');
    table.tableWidthState = String(widths.reduce((total, width) => total + width, 0));
    table.restoreColumnWidths();
  }

  /** Drops enforced widths so the table falls back to auto layout (drawer reset to default). */
  private clearColumnWidthsFromTable(): void {
    const table = this.tableRef();
    if (!table?.tableViewChild?.nativeElement) {
      return;
    }
    table.destroyStyleElement();
    table.columnWidthsState = undefined;
    table.tableWidthState = undefined;
    table.setResizeTableWidth('');
  }

  /**
   * Whether this grid can persist column layout to the backend.
   * Supports both modern endpoint-based designs (`endpointName`) and legacy
   * module/key pairs (`designModule` + `designKey`, e.g. payment plan list).
   */
  private hasDesignSaveTarget(): boolean {
    if (this.endpointName().trim()) {
      return true;
    }
    return !!this.designModule().trim() && !!this.designKey().trim();
  }

  /**
   * After a column resize, ask the user whether to save the grid design (legacy UX).
   *
   * Shown on every resize while a save target exists. Only one confirm dialog is
   * allowed at a time (`designSavePromptOpen`). Confirm opens
   * {@link KlGridColumnSettingsDrawer.promptSaveDesign} (preference dialog for legacy
   * grids); dismiss leaves the new widths in {@link effectiveColumns} until the user
   * saves manually or reloads.
   */
  private promptSaveDesignAfterResize(): void {
    if (this.designSavePromptOpen || !this.hasDesignSaveTarget()) {
      return;
    }
    this.designSavePromptOpen = true;
    this.confirmDialog
      .open({
        title: 'column_width_changed',
        description: 'save_grid_design_question',
        headerLabel: 'save_design',
        confirmLabel: 'save_design',
        icon: 'ph ph-floppy-disk',
        tone: 'info',
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((ok) => {
        this.designSavePromptOpen = false;
        if (ok) {
          this.columnSettingsDrawer()?.promptSaveDesign();
        }
      });
  }

  private resolveColumnWidth(width?: number | string): number | undefined {
    if (typeof width === 'number' && Number.isFinite(width)) {
      return width;
    }
    if (typeof width === 'string' && width !== '*' && width.trim() !== '') {
      const parsed = Number.parseInt(width.replace(/px$/i, '').trim(), 10);
      return Number.isFinite(parsed) ? parsed : undefined;
    }
    return undefined;
  }

  statusBadgeTone(row: object, col: GridColumn): KlStatusBadgeTone | null {
    if (!col.badgeTones || !col.binding) {
      return null;
    }
    const raw = cellRawValue(row, col.binding);
    if (typeof raw !== 'string' || raw === '') {
      return null;
    }
    return col.badgeTones[raw] ?? null;
  }

  showLoading(text: string): void {
    this.loading.set(true);
    this.loadingText.set(text);
  }

  hideLoading(): void {
    this.loading.set(false);
    this.loadingText.set('');
  }

  handleError(error: unknown): void {
    const message = extractApiErrorMessage(error as string | object | Error, this.translate.instant('error_occurred'));
    this.showAlert(message, 'error');
  }

  private showAlert(
    message: string,
    type: 'error' | 'warn' | 'warning' | 'success' | 'info' = 'error'
  ): void {
    this.alertShow.set(true);
    this.alertType.set(type === 'warn' ? 'warning' : type);
    this.alertMessage.set(message);
    this.recordFetchLoading.set(false);
  }

  // ─── Column width helper ───
  getColWidth(col: GridColumn): string {
    if (!col.width || col.width === '*') return 'auto';
    if (typeof col.width === 'number') return col.width + 'px';
    return col.width;
  }
}
